import React, { createContext, useState, useEffect, useContext } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check existing session on app load
  useEffect(() => {
    const checkExistingSession = () => {
      const token = localStorage.getItem('authToken');
      const userData = localStorage.getItem('userData');
      const chatToken = localStorage.getItem('jwt_token');
      const chatUserId = localStorage.getItem('current_user_id');
      const user = localStorage.getItem('user');
      const user_data = localStorage.getItem('user_data');

      // Priority: Check 'user' key first (chat component format)
      if (user) {
        try {
          const parsedUser = JSON.parse(user);
          setUser(parsedUser);
          
          // Ensure other tokens are set for compatibility
          if (!chatToken && token) {
            localStorage.setItem('jwt_token', token);
          } else if (!chatToken && !token) {
            // Generate a token placeholder for chat component
            localStorage.setItem('jwt_token', 'placeholder-token-' + Date.now());
          }
          
          if (!chatUserId && parsedUser.id) {
            localStorage.setItem('current_user_id', parsedUser.id.toString());
          }
          
          // Also set in regular format for compatibility
          if (!token) {
            localStorage.setItem('authToken', 'placeholder-token-' + Date.now());
          }
          if (!userData) {
            localStorage.setItem('userData', user);
          }
        } catch (error) {
          console.error('Error parsing user data from "user" key:', error);
          clearAllAuthData();
        }
      } 
      // Fallback: Check regular auth data
      else if (token && userData) {
        try {
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
          
          // Ensure chat format tokens are set for compatibility
          if (!chatToken) {
            localStorage.setItem('jwt_token', token);
          }
          if (!chatUserId && parsedUser.id) {
            localStorage.setItem('current_user_id', parsedUser.id.toString());
          }
          // Save as 'user' for chat component compatibility
          localStorage.setItem('user', userData);
        } catch (error) {
          console.error('Error parsing user data:', error);
          clearAllAuthData();
        }
      } 
      // Fallback: Check chat format data
      else if (chatToken && chatUserId && user_data) {
        try {
          const parsedUser = JSON.parse(user_data);
          setUser(parsedUser);
          // Also set in regular format for compatibility
          localStorage.setItem('authToken', chatToken);
          localStorage.setItem('userData', user_data);
          // Save as 'user' for chat component compatibility
          localStorage.setItem('user', user_data);
        } catch (error) {
          console.error('Error parsing chat user data:', error);
          clearAllAuthData();
        }
      }
      
      setLoading(false);
    };

    checkExistingSession();
  }, []);

  // Check if user has specific permission
  const hasPermission = (permission) => {
    if (!user) return false;
    
    // Super admin has all permissions
    if (user.role === 'super-admin') return true;
    
    // Check permissions array from backend
    if (user.permissions && Array.isArray(user.permissions)) {
      return user.permissions.includes(permission);
    }
    
    return false;
  };

  // Check if user has any of the given permissions
  const hasAnyPermission = (permissions) => {
    if (!user) return false;
    if (user.role === 'super-admin') return true;
    
    if (user.permissions && Array.isArray(user.permissions)) {
      return permissions.some(permission => user.permissions.includes(permission));
    }
    
    return false;
  };

  // Check if user has all of the given permissions
  const hasAllPermissions = (permissions) => {
    if (!user) return false;
    if (user.role === 'super-admin') return true;
    
    if (user.permissions && Array.isArray(user.permissions)) {
      return permissions.every(permission => user.permissions.includes(permission));
    }
    
    return false;
  };

  const isAdmin = () => {
    return user?.role === 'admin' || 
           user?.role === 'super-admin' || 
           user?.role?.includes('-admin');
  };

  const isSuperAdmin = () => {
    return user?.role === 'super-admin';
  };

  const isStateAdmin = () => {
    return user?.role === 'state-admin';
  };

  const clearAllAuthData = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('tokenExpires');
    localStorage.removeItem('jwt_token');
    localStorage.removeItem('current_user_id');
    localStorage.removeItem('user_data');
    localStorage.removeItem('user');
    setUser(null);
  };

  const login = (userData, token) => {
    // Store in all formats for compatibility
    // Regular app format
    localStorage.setItem('authToken', token);
    localStorage.setItem('userData', JSON.stringify(userData));
    
    // Chat component format (required)
    localStorage.setItem('jwt_token', token);
    localStorage.setItem('current_user_id', userData.id.toString());
    localStorage.setItem('user_data', JSON.stringify(userData));
    
    // Store as 'user' for chat component compatibility
    localStorage.setItem('user', JSON.stringify(userData));
    
    setUser(userData);
  };

  const logout = () => {
    clearAllAuthData();
  };

  const updateUser = (updatedUserData) => {
    const newUserData = { 
      ...user, 
      ...updatedUserData
    };
    
    // Update in all storage locations
    localStorage.setItem('userData', JSON.stringify(newUserData));
    localStorage.setItem('user_data', JSON.stringify(newUserData));
    localStorage.setItem('user', JSON.stringify(newUserData));
    
    // Update chat format if user ID changes
    if (updatedUserData.id && updatedUserData.id !== user?.id) {
      localStorage.setItem('current_user_id', updatedUserData.id.toString());
    }
    
    setUser(newUserData);
  };

  const value = {
    user,
    login,
    logout,
    updateUser,
    loading,
    isAuthenticated: !!user,
    isAdmin: isAdmin(),
    isSuperAdmin: isSuperAdmin(),
    isStateAdmin: isStateAdmin(),
    hasPermission,
    hasAnyPermission,
    hasAllPermissions
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext };